import { useEffect, useRef, useCallback, useState } from "react";

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_MS = 12 * 60 * 1000; // Show warning at 12 minutes

/**
 * Clears form state after inactivity to protect PII on shared computers.
 *
 * Usage:
 *   const { showWarning, resetTimer } = useSessionTimeout(onTimeout);
 *
 *   // onTimeout is called when the 15-min timer expires.
 *   // showWarning becomes true at 12 minutes so you can display a modal.
 *   // Any user interaction calls resetTimer() automatically.
 */
export function useSessionTimeout(onTimeout: () => void) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();
  const [showWarning, setShowWarning] = useState(false);

  const resetTimer = useCallback(() => {
    setShowWarning(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_MS);

    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, TIMEOUT_MS);
  }, [onTimeout]);

  useEffect(() => {
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    const handler = () => resetTimer();

    events.forEach((event) => window.addEventListener(event, handler));
    resetTimer(); // Start the timer

    return () => {
      events.forEach((event) => window.removeEventListener(event, handler));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [resetTimer]);

  return { showWarning, resetTimer };
}
